import { Classes, InputGroup, Intent, Position } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { ItemSelector } from 'vet-bones/components';
import { alphaSorter, isEqual } from 'vet-bones/utils';

import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE,
  PROJECT_DIAGRAM_ENTITY_DRAWER_DATATYPES_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
  getProjectDiagramEntityDrawerTableRowTestId,
} from 'src/constants/testIds';
import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { UpdateButton } from 'src/containers/ProjectDiagram/components/ProjectDiagramEntityDrawerButtons';
import { projectDatatypeItemsSelector } from 'src/selectors/projectDatatypeItems';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectDiagramAttributesTableEditRowProps = {
  attribute: AttributeTableItem;
  idx: number;
  onSubmit: (
    update: {
      attributeIri: string;
      datatypes: string[];
      newLabel: string;
    } | null
  ) => void;
};

export const ProjectDiagramAttributesTableEditRow: React.VFC<
  ProjectDiagramAttributesTableEditRowProps
> = ({ attribute, idx, onSubmit }) => {
  const { projectId } = useRouterParams();
  const datatypeItems = useReduxSelector(
    projectDatatypeItemsSelector(projectId)
  );

  const [localLabel, setLocalLabel] = React.useState(attribute.text);
  const [localDatatypes, setLocalDatatypes] = React.useState(attribute.ranges);

  const buttonText =
    localDatatypes.length > 0
      ? localDatatypes
          .map(({ text }) => text)
          .sort(alphaSorter)
          .join(', ')
      : copy.components.projectDiagram.attributesTable.noType;

  let tooltipContent = '';
  if (!localLabel) {
    tooltipContent = copy.components.projectDiagram.attributesTable.missingName;
  }
  const disabled = Boolean(tooltipContent);
  // we actually don't have to check for datatypes, since the selector does
  // not let you clear, so an empty state is only possible if it was already empty

  return (
    <tr
      data-testid={getProjectDiagramEntityDrawerTableRowTestId(
        attribute.value,
        PROJECT_DIAGRAM_ENTITY_DRAWER_ATTRIBUTE_TYPE,
        idx
      )}
    >
      <td>
        <InputGroup
          intent={!localLabel ? Intent.DANGER : undefined}
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            setLocalLabel(e.currentTarget.value)
          }
          placeholder={attribute.text}
          value={localLabel}
        />
      </td>
      <td>
        <ItemSelector.Multi
          buttonClassName={classNames(Classes.SMALL, Classes.TEXT_DISABLED)}
          buttonMinimal
          buttonRightIcon={null}
          buttonText={buttonText}
          filterInputProps={
            {
              'data-testid':
                TEST_IDS.projectDiagram.entityDrawer.selectorFilter,
              placeholder: copy.components.defaults.filter,
            } as any
          }
          items={datatypeItems}
          itemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          onSelect={setLocalDatatypes}
          popoverProps={{
            position: Position.BOTTOM,
          }}
          selected={localDatatypes}
          testId={getProjectDiagramEntityDrawerSelectorTestId(
            PROJECT_DIAGRAM_ENTITY_DRAWER_DATATYPES_TYPE
          )}
        />
      </td>
      <td />
      <td>
        <UpdateButton
          disabled={disabled}
          onClick={() => {
            if (disabled) {
              return;
            }

            const needsUpdate =
              !isEqual(localDatatypes, attribute.ranges) ||
              localLabel !== attribute.text;

            onSubmit(
              needsUpdate
                ? {
                    attributeIri: attribute.value,
                    datatypes: localDatatypes.map((item) => item.value),
                    newLabel: localLabel,
                  }
                : null
            );
          }}
          tooltip={tooltipContent}
        />
      </td>
    </tr>
  );
};
